import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@wineryxplorer/api';
import { JsonViewer } from '../ui/JsonViewer';
import { formatBytes, formatDate } from './cloudinaryFormatters';

type CloudinaryResourceDetailData =
  inferRouterOutputs<AppRouter>['media']['getCloudinaryResource'];

interface MediaReference {
  entityType: string;
  referenceType: string;
}

export interface CloudinaryResourceDetailProps {
  selectedResource: string | null;
  resourceDetail: CloudinaryResourceDetailData | undefined;
  isLoading: boolean;
  onRequestDelete: () => void;
}

export const CloudinaryResourceDetail = ({
  selectedResource,
  resourceDetail,
  isLoading,
  onRequestDelete,
}: CloudinaryResourceDetailProps) => (
  <div className="bg-gray-50 rounded-lg shadow">
    <div className="p-4 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900">Resource Details</h3>
    </div>

    <div className="p-4 h-[600px] overflow-y-auto">
      {!selectedResource ? (
        <div className="text-center text-gray-500 py-8">Select a resource to view details</div>
      ) : isLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading details...</p>
        </div>
      ) : resourceDetail ? (
        <div className="space-y-6">
          {/* Preview */}
          <div className="text-center">
            {resourceDetail.resourceType === 'image' ? (
              <img
                src={resourceDetail.secureUrl}
                alt={resourceDetail.publicId}
                className="max-w-full max-h-48 mx-auto rounded-lg shadow"
              />
            ) : resourceDetail.resourceType === 'video' ? (
              <video
                src={resourceDetail.secureUrl}
                controls
                className="max-w-full max-h-48 mx-auto rounded-lg shadow"
              />
            ) : (
              <div className="w-32 h-32 bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                <span className="text-4xl">📄</span>
              </div>
            )}
          </div>

          {/* Delete Button */}
          <div className="flex justify-center">
            <button
              onClick={onRequestDelete}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              <span>Delete Now</span>
            </button>
          </div>

          {/* Cloudinary Metadata */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Cloudinary Metadata</h4>
            <div className="bg-gray-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-gray-600">Public ID:</span>
                <span className="font-mono text-xs break-all">{resourceDetail.publicId}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-gray-600">Format:</span>
                <span>{resourceDetail.format}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-gray-600">Size:</span>
                <span>{formatBytes(resourceDetail.bytes)}</span>
              </div>
              {resourceDetail.width && resourceDetail.height && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Dimensions:</span>
                  <span>
                    {resourceDetail.width} × {resourceDetail.height}
                  </span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <span className="text-gray-600">Created:</span>
                <span>{formatDate(resourceDetail.createdAt)}</span>
              </div>
              {resourceDetail.folder && (
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Folder:</span>
                  <span className="font-mono text-xs">{resourceDetail.folder}</span>
                </div>
              )}
            </div>
          </div>

          {/* Context/Metadata */}
          {(resourceDetail.context || resourceDetail.metadata) && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Context & Metadata</h4>
              {resourceDetail.context && (
                <div className="mb-4">
                  <h5 className="text-xs font-medium text-gray-700 mb-2">Context:</h5>
                  <JsonViewer data={resourceDetail.context} maxDepth={3} />
                </div>
              )}
              {resourceDetail.metadata && (
                <div>
                  <h5 className="text-xs font-medium text-gray-700 mb-2">Metadata:</h5>
                  <JsonViewer data={resourceDetail.metadata} maxDepth={3} />
                </div>
              )}
            </div>
          )}

          {/* Database Association */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Database Association</h4>
            {resourceDetail.dbRecord ? (
              <div className="bg-green-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Entity Type:</span>
                  <span className="capitalize">{resourceDetail.dbRecord.entityType}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Entity ID:</span>
                  <span className="font-mono text-xs">{resourceDetail.dbRecord.entityId}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Status:</span>
                  <span
                    className={`capitalize ${
                      resourceDetail.dbRecord.status === 'active'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {resourceDetail.dbRecord.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Published:</span>
                  <span
                    className={
                      resourceDetail.dbRecord.isPublished ? 'text-green-600' : 'text-gray-600'
                    }
                  >
                    {resourceDetail.dbRecord.isPublished ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Uploaded By:</span>
                  <span>
                    {resourceDetail.dbRecord.uploadedBy.name ||
                      resourceDetail.dbRecord.uploadedBy.email}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-gray-600">Created:</span>
                  <span>{formatDate(resourceDetail.dbRecord.createdAt)}</span>
                </div>
              </div>
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex">
                  <div className="text-yellow-400">⚠️</div>
                  <div className="ml-3">
                    <h5 className="text-sm font-medium text-yellow-800">Orphaned Resource</h5>
                    <p className="text-sm text-yellow-700 mt-1">
                      This resource exists in Cloudinary but has no corresponding database record.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* References */}
          {resourceDetail.references && (
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">References</h4>
              {resourceDetail.references.isReferenced ? (
                <div className="bg-primary-50 rounded-lg p-3 space-y-2 text-sm">
                  <p className="text-primary-800 font-medium">
                    This media is referenced by {resourceDetail.references.references.length}{' '}
                    entities:
                  </p>
                  {resourceDetail.references.references.map(
                    (ref: MediaReference, index: number) => (
                      <div key={index} className="text-primary-700">
                        • {ref.entityType} ({ref.referenceType})
                      </div>
                    )
                  )}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                  No references found
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-red-500 py-8">Failed to load resource details</div>
      )}
    </div>
  </div>
);
